import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api';
import { useStream } from '../hooks/useApi';
import type { WatchItem } from './types';

/**
 * The account's watchlist, shared by every star on every page and by the
 * panel. Quotes refresh on the live stream and once a minute, so the panel's
 * prices tick like a stock app's.
 */

type Kind = WatchItem['kind'];
type Ctx = {
  items: WatchItem[];
  loading: boolean;
  isWatched: (kind: Kind, ref: string) => boolean;
  toggle: (item: { kind: Kind; ref: string; gameId?: string | null; label: string }) => Promise<void>;
  reload: () => void;
};

const WatchlistContext = createContext<Ctx | null>(null);

export function WatchlistProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<WatchItem[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    api
      .watchlist()
      .then((r) => setItems(r.items))
      .catch(() => {
        /* the panel keeps its last quotes; the next tick tries again */
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 60_000);
    return () => clearInterval(t);
  }, [reload]);

  useStream((event) => {
    if (['line_move', 'model_move', 'live_stats', 'graded', 'picks_locked'].includes(event.type)) reload();
  });

  const keys = useMemo(() => new Set(items.map((i) => `${i.kind}:${i.ref}`)), [items]);
  const isWatched = useCallback((kind: Kind, ref: string) => keys.has(`${kind}:${ref}`), [keys]);

  const toggle = useCallback(
    async (item: { kind: Kind; ref: string; gameId?: string | null; label: string }) => {
      const on = keys.has(`${item.kind}:${item.ref}`);
      // Optimistic: the star answers the click immediately.
      setItems((prev) =>
        on
          ? prev.filter((i) => !(i.kind === item.kind && i.ref === item.ref))
          : [{ kind: item.kind, ref: item.ref, gameId: item.gameId ?? null, label: item.label, createdAt: new Date().toISOString(), quote: null }, ...prev],
      );
      try {
        if (on) await api.unwatch(item.kind, item.ref);
        else await api.watch(item);
      } finally {
        reload();
      }
    },
    [keys, reload],
  );

  return (
    <WatchlistContext.Provider value={{ items, loading, isWatched, toggle, reload }}>{children}</WatchlistContext.Provider>
  );
}

export function useWatchlist(): Ctx {
  const ctx = useContext(WatchlistContext);
  if (!ctx) throw new Error('useWatchlist outside WatchlistProvider');
  return ctx;
}
